import React, { useContext, useEffect, useMemo, useState } from "react";
import moment from "moment";
import _ from "lodash";
import DatePicker, { registerLocale } from "react-datepicker";
import { IconButton } from "@material-ui/core";
import ErrorIcon from "@material-ui/icons/Error";
import ptBR from "date-fns/locale/pt-BR";
import "react-datepicker/dist/react-datepicker.css";
import { FullScreenLoading } from "../../../_core/_components/fullscreen-loading";
import Table from "../../../_core/_components/table";
import {
  Button,
  Form,
  Input,
  Modal,
  Option,
  Select,
} from "../../../_core/_components";
import * as S from "./styles";
import { handleErrors, warning, success } from "../../../_core/services/toast";
import api from "../../../_core/api";
import { Person } from "@material-ui/icons";

registerLocale("pt-BR", ptBR);

export const HistoricoComunicacoes: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [unidadeEconomicas, setUnidadesEconomicas] = useState<Option[]>([]);
  const [nomeFantasia, setNomeFantasia] = useState("");
  const [periodo, setPeriodo] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [fiadores, setFiadores] = useState<any[]>([]);
  const [errorSelection, setErrorSelection] = useState("");
  const [luc, setLuc] = useState("");
  const [tipoComunicado, setTipoComunicado] = useState<Option[]>(undefined);
  const [tipoComunicadoList, setTipoComunicadoList] = useState<Option[]>([]);
  const [shoppingsList, setShoppingsList] = useState<Option[]>([]);
  const [statusComunicacao, setStatusComunicacao] =
    useState<Option[]>(undefined);
  const [eventos, setEventos] = useState<any[]>([]);
  const statusOptions = useMemo(
    () =>
      _.uniq([
        "AGUARDANDO_RETORNO",
        "INCLUIDO",
        "AGUARDANDO_ENVIO",
        "RETORNO_COM_ERRO",
        "ENVIADO",
      ]).map((status) => ({
        value: status,
        label: _.startCase(status.toLowerCase()),
      })),
    []
  );

  useEffect(() => {
    getData();
  }, []);

  const [startDate, endDate] = periodo;

  const handlePeriodoChange = (
    dates: [Date | null, Date | null] | Date | null
  ) => {
    if (!Array.isArray(dates)) {
      return;
    }

    const [start, end] = dates;
    setPeriodo([start, end]);
  };

  const getData = async () => {
    setIsLoading(true);
    try {
      const { data: dataTipoComunicado } = await api.post(
        `/api/cartas/tipoComunicado/buscar`,
        {
          nomeTipoComunicado: "",
          codigoTipoComunicado: "",
        }
      );
      const { data: dataShoppings } = await api.get(
        "/api/cartas/shopping/buscar"
      );
      setShoppingsList(
        dataShoppings.content.map((e) => ({
          value: e.unidadeEconomica,
          label: `${e.nomeShopping} (${e.unidadeEconomica})`,
        }))
      );
      setTipoComunicadoList(
        dataTipoComunicado.content.map((e) => ({
          label: e.nomeTipoComunicado,
          value: e.idTipoComunicado,
        }))
      );
    } catch (error) {
      handleErrors(error);
    }
    setIsLoading(false);
  };

  const handleErrorModal = (item: any) => {
    setErrorSelection(item.observacao);
  };

  const onClickSearch = async () => {
    const missingFields: string[] = [];
    if (!unidadeEconomicas.length) {
      missingFields.push("Shoppings");
    }
    if (!startDate || !endDate) {
      missingFields.push("Periodo (data envio)");
    }
    if (!statusComunicacao?.length) {
      missingFields.push("Status comunicacao");
    }
    if (!tipoComunicado?.length) {
      missingFields.push("Tipo comunicado");
    }

    if (missingFields.length) {
      warning(`Preencha os campos obrigatorios: ${missingFields.join(", ")}`);
      return;
    }

    setIsLoading(true);
    try {
      const body: any = {
        unidadeEconomicas: unidadeEconomicas
          .map((v) => v.value)
          .filter((v) => v !== "todos"),
        idTipoComunicado: tipoComunicado
          ?.map((v) => v.value)
          .filter((v) => v !== "todos"),
        dataInicio: startDate
          ? moment(startDate).format("YYYY-MM-DD")
          : undefined,
        dataFim: endDate ? moment(endDate).format("YYYY-MM-DD") : undefined,
        statusCode: statusComunicacao
          ?.map((v) => v.value)
          .filter((v) => v !== "todos"),
        nomeFantasia,
        luc,
      };
      if (!nomeFantasia) {
        delete body.nomeFantasia;
      }
      if (!luc) {
        delete body.luc;
      }

      const { data } = await api.post("/api/cartas/eventos/buscar", body);
      setEventos(data?.content || []);
      if (data?.content.length === 0)
        success("Nenhum dado encontrado com os filtros especificados.");
    } catch (error) {
      handleErrors(error);
    }
    setIsLoading(false);
  };
  const renderedData = useMemo(() => {
    return eventos.map((item) => {
      const statusCode = item?.status?.code || "";
      const statusLabel =
        item?.status?.nomeStatus ||
        (statusCode ? _.startCase(statusCode.toLowerCase()) : "-");

      return {
        nmShopping: item?.shopping || "-",
        nomFantasia: item?.nomeFantasia || "-",
        razaoSocial: item?.razaoSocial || "-",
        luc: item?.luc || "-",
        nmBoleto: item?.numeroBoleto || item?.idBoleto || "-",
        strVencimento: moment(item?.vencimentoBoleto).isValid()
          ? moment(item?.vencimentoBoleto).format("DD/MM/YYYY")
          : item?.vencimentoBoleto || "-",
        tipoComunicacao:
          item?.comunicado?.tipoComunicado?.nomeTipoComunicado ||
          item?.comunicado?.descricaoComunicado ||
          "-",
        dataEnvio: moment(item?.dataEvento).isValid()
          ? moment(item?.dataEvento).format("DD/MM/YYYY")
          : item?.dataEvento || "-",
        status: statusLabel,
        fiadores: item.fiadores.length ? (
          <IconButton
            aria-controls="simple-menu"
            aria-haspopup="true"
            color="primary"
            component="span"
            onClick={() => setFiadores(item.fiadores)}
          >
            <Person />
          </IconButton>
        ) : null,
        error: item.observacao ? (
          <IconButton
            aria-controls="simple-menu"
            aria-haspopup="true"
            component="span"
            color="primary"
            onClick={() => handleErrorModal(item)}
          >
            <ErrorIcon />
          </IconButton>
        ) : null,
      };
    });
  }, [eventos]);

  const modalContentFiador = useMemo(() => {
    const arrayInput: React.ReactNode[] = [];
    fiadores.forEach((fiador) => {
      arrayInput.push(
        <Input
          label="Nome"
          state={[fiador.nomeFiador ?? "", () => null]}
          disabled
        />
      );
      arrayInput.push(
        <Input
          label="CPF/CNPJ"
          state={[fiador.cpfcnpjFiador ?? "", () => null]}
          disabled
        />
      );
      arrayInput.push(
        <Input
          label="Endereço"
          state={[fiador?.endereco ?? "", () => null]}
          disabled
        />
      );
    });
    return <Form items={arrayInput} />;
  }, [fiadores]);

  const handleCloseModal = () => {
    setFiadores([]);
    setErrorSelection("");
  };

  const modalContentError = useMemo(() => {
    return <div>{errorSelection}</div>;
  }, [errorSelection]);

  return (
    <>
      <FullScreenLoading isEnabled={isLoading} />
      {(fiadores.length > 0 || errorSelection) && (
        <Modal
          title={errorSelection ? "Detalhes do Erro" : "Detalhes do Fiador"}
          onClose={() => handleCloseModal()}
          content={errorSelection ? modalContentError : modalContentFiador}
        />
      )}

      <Form
        items={[
          <Select
            state={[unidadeEconomicas, setUnidadesEconomicas]}
            options={shoppingsList}
            placeholder="Shoppings *"
            todos
            checkedByDefault
          />,
          <Input
            label="Nome fantasia"
            state={[nomeFantasia, setNomeFantasia]}
          />,
          <S.DatePickerWrapper>
            <label>Periodo (data envio) *</label>
            <DatePicker
              selectsRange
              startDate={startDate}
              endDate={endDate}
              onChange={handlePeriodoChange}
              className="date-range-input"
              shouldCloseOnSelect={false}
              locale="pt-BR"
              value={`${
                startDate
                  ? moment(startDate).format("DD/MM/YYYY")
                  : "DD/MM/YYYY"
              } - ${
                endDate ? moment(endDate).format("DD/MM/YYYY") : "DD/MM/YYYY"
              }`}
            />
          </S.DatePickerWrapper>,
          <Input label="LUC" state={[luc, setLuc]} />,
          <Select
            state={[tipoComunicado, setTipoComunicado]}
            options={tipoComunicadoList}
            placeholder="Tipo comunicado *"
            todos
            checkedByDefault
          />,
          <Select
            state={[statusComunicacao, setStatusComunicacao]}
            options={statusOptions}
            placeholder="Status comunicacao *"
            todos
            checkedByDefault
          />,
        ]}
        submitButton={<Button onClick={onClickSearch} text="Pesquisar" />}
      />

      {renderedData.length > 0 && (
        <>
          <Table
            columns={[
              { label: "Nome do Shopping", key: "nmShopping", orderable: true },
              { label: "Nome fantasia", key: "nomFantasia", orderable: true },
              { label: "Razão social", key: "razaoSocial", orderable: true },
              { label: "Luc", key: "luc", orderable: true },
              { label: "Boleto", key: "nmBoleto", orderable: true },
              { label: "Vencimento", key: "strVencimento", orderable: true },
              {
                label: "Tipo comunicação",
                key: "tipoComunicacao",
                orderable: true,
              },
              { label: "Data envio", key: "dataEnvio", orderable: true },
              { label: "Status", key: "status", orderable: true },
              { label: "Fiadores", key: "fiadores", orderable: false },
              { label: "Erro", key: "error", orderable: false },
            ]}
            data={renderedData}
          />
        </>
      )}
    </>
  );
};
